import { useAccess } from '@umijs/max';
import { Button, message } from 'antd';
import React, { useRef, useState } from 'react';
import { isCenterBackedContract } from '@/modules/zy-crm/utils/contract-center';
import {
  PERMISSION_ACTION_CODES,
  PERMISSION_RESOURCE_CODES,
} from '@/permissions/definitions';
import { hasActionAccess } from '@/permissions/helpers';
import { repairContractProjection } from '@/services/bff/contract.api';

export type ContractCenterSyncActionContract = {
  uuid?: string;
  centerBacked?: boolean;
  contractTypeUuid?: string;
  centerStatus?: string;
};

export type ContractCenterSyncActionProps = {
  contract?: ContractCenterSyncActionContract | null;
  onSynced?: () => void | Promise<void>;
  mode?: 'link' | 'menu-item';
};

export function ContractCenterSyncAction({
  contract,
  onSynced,
  mode = 'link',
}: ContractCenterSyncActionProps) {
  const access = useAccess() as Record<string, boolean>;
  const [loading, setLoading] = useState(false);
  const inflightRef = useRef(false);
  const canRepair = hasActionAccess(
    access,
    PERMISSION_RESOURCE_CODES.contractsProjectionRepair,
    PERMISSION_ACTION_CODES.contractsProjectionRepair.repair,
  );

  if (!isCenterBackedContract(contract) || !canRepair) {
    return null;
  }

  const handleSync = async () => {
    if (inflightRef.current || loading) {
      return;
    }
    const uuid = String(contract?.uuid || '').trim();
    if (!uuid) {
      message.warning('当前合同缺少 UUID，无法同步');
      return;
    }
    inflightRef.current = true;
    setLoading(true);
    try {
      await repairContractProjection(uuid);
      message.success('已同步合同中心');
      await onSynced?.();
    } catch {
      return;
    } finally {
      inflightRef.current = false;
      setLoading(false);
    }
  };

  if (mode === 'menu-item') {
    return (
      <span
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void handleSync();
        }}
      >
        同步合同中心
      </span>
    );
  }

  return (
    <Button
      type="link"
      size="small"
      loading={loading}
      onClick={() => void handleSync()}
    >
      同步合同中心
    </Button>
  );
}
